import axios from "ts-axios-new";
import {ElMessageBox} from "element-plus";
import {initI18n} from "./init";

const I18N_KEYS = Object.keys(initI18n());

export function i18nKeys() {
    return I18N_KEYS;
}

function escapeHtml(value: string) {
    return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

export function createI18nFields(fields: Record<string, Record<string, string>>) {
    const result: Record<string, Record<string, string>> = {};
    Object.keys(fields).forEach(field => {
        result[field] = {...initI18n(), ...(fields[field] || {})};
    });
    return result;
}

export async function translateI18nFields(
    fields: Record<string, Record<string, string>>,
    overwrite = false,
    targets: string[] = I18N_KEYS.filter(lang => lang !== 'EN'),
) {
    const payload = {
        source_lang: 'EN',
        targets,
        fields: createI18nFields(fields),
        overwrite,
    };
    try {
        const res = await axios.post('/cms/v1/i18n/translate', payload);
        return res.data.data;
    } catch (err: any) {
        console.error('[i18n translate] request failed', {
            url: '/cms/v1/i18n/translate',
            method: 'POST',
            status: err?.response?.status,
            response: err?.response?.data,
            message: err?.response?.data?.status?.message || err?.message,
            payload,
        });
        throw err;
    }
}

export function buildTranslatePreview(
    translatedFields: Record<string, Record<string, string>>,
    errors: Record<string, Record<string, string>>,
    labels: Record<string, string>,
    originals: Record<string, Record<string, string>> = {},
    overwrite = false,
) {
    const rows = Object.keys(translatedFields || {}).map(field => {
        const translated = translatedFields[field] || {};
        const original = originals[field] || {};
        const fieldErrors = errors && errors[field] ? errors[field] : {};
        const updatingCount = I18N_KEYS.filter(lang => {
            const current = original[lang] || '';
            const next = translated[lang] || '';
            return lang !== 'EN' && next && (overwrite || !current);
        }).length;
        const detailRows = I18N_KEYS.filter(lang => lang !== 'EN').map(lang => {
            const current = original[lang] || '';
            const next = translated[lang] || '';
            const willApply = !!next && (overwrite || !current);
            const status = willApply
                ? (current ? '将覆盖' : '将填充')
                : fieldErrors[lang]
                    ? `失败：${fieldErrors[lang]}`
                    : current
                        ? '保留已有'
                        : '无结果';
            const statusColor = willApply ? '#e6a23c' : fieldErrors[lang] || !current ? '#f56c6c' : '#909399';
            return `<tr>
                <td style="padding: 6px 8px; border-bottom: 1px solid #ebeef5; white-space: nowrap">${lang}</td>
                <td style="padding: 6px 8px; border-bottom: 1px solid #ebeef5">${escapeHtml(current)}</td>
                <td style="padding: 6px 8px; border-bottom: 1px solid #ebeef5">${escapeHtml(willApply ? next : '')}</td>
                <td style="padding: 6px 8px; border-bottom: 1px solid #ebeef5; color: ${statusColor}; white-space: nowrap">${escapeHtml(status)}</td>
            </tr>`;
        }).join('');
        return `<div style="margin-bottom: 12px">
            <strong>${escapeHtml(labels[field] || field)}</strong>
            <div style="margin: 4px 0 8px; color: #606266">
                将更新 ${updatingCount} 个语言，失败 ${Object.keys(fieldErrors).length} 个
            </div>
            <table style="width: 100%; border-collapse: collapse; table-layout: fixed">
                <thead>
                    <tr style="background: #f5f7fa">
                        <th style="padding: 6px 8px; text-align: left; width: 80px">语言</th>
                        <th style="padding: 6px 8px; text-align: left">当前内容</th>
                        <th style="padding: 6px 8px; text-align: left">翻译结果</th>
                        <th style="padding: 6px 8px; text-align: left; width: 120px">状态</th>
                    </tr>
                </thead>
                <tbody>${detailRows}</tbody>
            </table>
        </div>`;
    });
    return rows.join('') || '没有可用翻译';
}

export function confirmTranslatePreview(message: string) {
    return ElMessageBox.confirm(message, '翻译预览', {
        cancelButtonText: '取消',
        confirmButtonText: '应用',
        dangerouslyUseHTMLString: true,
        customClass: 'i18n-translate-preview',
        type: 'info',
    });
}
